import { useCallback, useEffect, useMemo, useState } from 'react';

export default function useLocalStorageState<T>(
    key: string,
    defaultValue: T,
    valueConverterRaw?: (value: string | null) => T,
): [T, (value: T) => void] {
    const readRaw = useCallback(
        () => localStorage.getItem(key) ?? `${defaultValue}`,
        [key, defaultValue],
    );

    const [value, setValue] = useState<string>(readRaw);

    // Re-read when the key changes (e.g. dynamic keys scoped per workspace)
    useEffect(() => {
        setValue(readRaw());
    }, [readRaw]);

    useEffect(() => {
        const handleStorage = () => setValue(readRaw());
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [readRaw]);

    const valueConverter = useMemo(
        () => valueConverterRaw ?? ((value: string | null) => (value as unknown as T)),
        [valueConverterRaw]
    );

    const updateValue = useCallback(
        (value: any) => {
            window.localStorage.setItem(key, value);
            setValue(value);
        },
        [key]
    );

    const memoValue = useMemo(
        () => {
            return valueConverter(value);
        },
        [value, valueConverter]
    );

    return [memoValue, updateValue];
}